'use client'
import { memo, useMemo, useRef, type FC } from 'react'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import { designGrid, mq, presets } from '@/styles'
import { Img, SliderFlickity } from './base'
import { fadeFromLeftVariants } from '@/utils'
import { useTranslator } from '@/hooks'
import type { TypeCmsTestimonial } from '@/schema'

type Props = {
  data: TypeCmsTestimonial[]
}

const getItems = (data: Props['data']) => data.map((item, idx) => (
  <div key={idx + item?.authorName}>
    <article>
      {item.medium?.image ? <Img media={item.medium.image} /> : <div className='placeholder' />}
      <h3>{item.authorName}{item?.authorLocation ? ` - ${item.authorLocation}` : ''}</h3>
      <p>{item.text}</p>
    </article>
  </div>
))

export const AsideTestimonials:FC<Props> = memo(({ data }) => {
  const { t } = useTranslator()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })
  const Items = getItems(data)
  const headingId = useMemo(() => t('project.internal_page_links')?.[4]?.id?.substring(1) as string, [t])

  return (
    <Aside aria-labelledby={headingId} ref={ref}>
      <motion.h2 id={headingId} variants={fadeFromLeftVariants} initial='initial' animate={isInView ? 'animate' : 'initial'}>
        {t('project.section_testimonials_title')}
      </motion.h2>
      <SliderFlickity options={{ autoPlay: false, wrapAround: false, pageDots: false, cellAlign: 'left', prevNextButtons: true }} customAutoPlay={false} customAutoPlaySpeed={.3}>
        {Items}
      </SliderFlickity>
    </Aside>
  )
})

const Aside = styled.aside`
  ${designGrid({})}
  margin: 75px 0 100px;
  overflow-x: hidden;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin: 90px 0 190px;
  }

  h2 {
    grid-column: 1 / span 6;
    margin-bottom: 30px;
    text-align: center;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      margin-bottom: 90px;
      text-align: left;
    }
  }

  .slider-wrapper {
    grid-column: 1 / span 6;
    padding: 0 45px;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      padding: 0;
    }
  }

  div.flickity-cell {
    margin-right: 100px;
    width: 280px;
  }

  article {
    align-items: center;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 20px;
    position: relative;
    width: 280px;
    z-index: 2;

    ${mq.greaterThan('tablet')} {
      width: 300px;
    }
  }

  img,
  .placeholder {
    aspect-ratio: 1/1;
    background-color: var(--color-black);
    border-radius: 90px;
    height: 120px;
    margin: 0 auto 30px;
    position: relative;
    width: 120px;

    ${mq.greaterThan('tablet')} {
      margin: 0 auto 10px 0;
    }
  }

  .placeholder {
    opacity: .2;
  }

  h3,
  p {
    text-align: center;

    ${mq.greaterThan('tablet')} {
      text-align: left;
    }
  }

  p {
    ${presets.font18_20()}
  }

  h3 {
    opacity: .5;
    order: 2;
    ${presets.font15_18()}
  }
`
